import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SectionAlert, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { ScrubAddressModal } from '@app/components/ScrubAddressModal';
import { selectHasFiber, selectUser } from '@app/redux/auth';
import { fetchScrubbedAddress, resetScrubbedAddress, selectScrubbedAddress } from '@app/redux/scrubbedAddress';
import { selectUserInfo } from '@app/redux/userInfo';

import { AddressInput, ContactMethodLabels, EmailInput } from '@mfe/shared/schema-types';

import { useAddressFormValues, usePrepopulateContactInfo, useSaveInIra } from '../../hooks';
import EditableAddress from './EditableAdress';
import EditableEmail from './EditableEmail';
import InfoCard from './InfoCard';
import { hasEquipmentAlert } from '../../utils';
import { formatAddressOnTwoLines } from '../../utils/formatAddressOnTwoLines';
import { ContactInfoSection, Section } from '../../types';
import { CountryCode } from '@viasat/res-apps-lib/build/types/bv';

import styles from './index.module.scss';

type ContactInformationProps = {
  setEditableSection: React.Dispatch<React.SetStateAction<Section>>;
  editableSection: Section;
};

const ContactInformation = ({ setEditableSection, editableSection }: ContactInformationProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Disconnect');

  const [showScrubModal, setShowScrubModal] = useState(false);
  const [loadingSection, setLoadingSection] = useState<Section>(null);
  const [addressFormKey, setAddressFormKey] = useState(0);
  const [emailFormKey, setEmailFormKey] = useState(0);

  const saveInIra = useSaveInIra();
  const { address, email, loading } = usePrepopulateContactInfo();
  const { inputAddress, handleAddressChange, resetAddress } = useAddressFormValues();
  const [inputEmail, setInputEmail] = useState<string>('');
  const { wasUpdateSuccessful, error } = useSelector(selectUserInfo);

  const handleEmailChange = (event: FormEvent<HTMLInputElement>) => {
    setInputEmail(event.currentTarget.value);
  };

  const scrubAddressState = useSelector(selectScrubbedAddress);
  const { loading: scrubLoading, scrubbedAddress } = scrubAddressState;

  const hasFiber = useSelector(selectHasFiber);
  const { user } = useSelector(selectUser);
  const { indoorUnit } = user;
  const showEquipmentAlert = hasEquipmentAlert(indoorUnit, t);

  const handleAddressSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(fetchScrubbedAddress(inputAddress));
    setShowScrubModal(true);
  };

  const handleAddressRetry = () => {
    dispatch(fetchScrubbedAddress(inputAddress));
    setShowScrubModal(true);
  };

  const formattedAddress = useMemo(
    () => formatAddressOnTwoLines(scrubbedAddress.rawAddress),
    [scrubbedAddress.recommendation, scrubbedAddress.processStatus]
  );

  const closeAddressEditMode = () => {
    setShowScrubModal(false);
    resetAddress();
    dispatch(resetScrubbedAddress());
    setAddressFormKey((prev) => prev + 1);
    setEditableSection(null);
  };

  const closeEmailEditMode = () => {
    setInputEmail('');
    setEmailFormKey((prev) => prev + 1);
    setEditableSection(null);
  };

  const handleEmailSave = (e: FormEvent<HTMLFormElement>) => {
    setLoadingSection(ContactInfoSection.Email);
    e.preventDefault();
    const labels = [ContactMethodLabels.Invoice, ContactMethodLabels.Primary];
    const email: EmailInput = { email: inputEmail, labels };
    saveInIra({ email });
    setEditableSection(null);
  };

  const handleScrubSave = () => {
    setLoadingSection(ContactInfoSection.Address);
    const labels = [ContactMethodLabels.Shipping];
    const rawScrubbedAddress = {
      addressLines: scrubbedAddress.rawAddress.addressLines,
      countryCode: CountryCode.US,
      region: scrubbedAddress.rawAddress.region as string,
      municipality: scrubbedAddress.rawAddress.municipality,
      postalCode: scrubbedAddress.rawAddress.postalCode as string,
    };
    const addresses: AddressInput[] = [{ ...rawScrubbedAddress, labels }];
    saveInIra({ addresses });
    setShowScrubModal(false);
    setEditableSection(null);
  };

  useEffect(() => {
    if (!loadingSection) {
      return;
    }

    if (wasUpdateSuccessful) {
      if (loadingSection === ContactInfoSection.Address) {
        closeAddressEditMode();
      }

      if (loadingSection === ContactInfoSection.Email) {
        closeEmailEditMode();
      }

      setLoadingSection(null);
      return;
    }

    if (error) {
      setLoadingSection(null);
      setShowScrubModal(false);
    }
  }, [error, loadingSection, wasUpdateSuccessful]);

  return (
    <div className={styles['contactInformation']}>
      <Txt variant="bodyLargeBold" mb="8px">
        {t('contactInfo.title')}
      </Txt>
      <Txt variant="bodySmallRegular">
        {hasFiber ? t('contactInfo.description.fiber') : t('contactInfo.description.satellite')}
      </Txt>

      {!hasFiber && (
        <>
          <hr className={styles['separator']} />
          <InfoCard
            title={t('contactInfo.shippingAddress.label')}
            loading={loading && loadingSection === ContactInfoSection.Address}
            values={address}
            isEditable={editableSection === ContactInfoSection.Address}
            handleEdit={() => setEditableSection(ContactInfoSection.Address)}
            handleSave={handleAddressSave}
            handleCancel={closeAddressEditMode}
            formId="address-form"
          >
            <EditableAddress key={addressFormKey} handleSubmit={handleAddressSave} handleChange={handleAddressChange} />
          </InfoCard>

          {showScrubModal && (
            <ScrubAddressModal
              isLoading={scrubLoading}
              isModalOpen={showScrubModal}
              handleClose={() => setShowScrubModal(false)}
              handleRetry={handleAddressRetry}
              handleSave={handleScrubSave}
              suggestedAddress={formattedAddress}
              namespace="ScrubAddress"
            />
          )}
        </>
      )}
      <InfoCard
        title={t('contactInfo.email.label')}
        loading={loading && loadingSection === ContactInfoSection.Email}
        values={[email]}
        isEditable={editableSection === ContactInfoSection.Email}
        handleEdit={() => setEditableSection(ContactInfoSection.Email)}
        handleSave={handleEmailSave}
        handleCancel={closeEmailEditMode}
        formId="email-form"
      >
        <EditableEmail key={emailFormKey} handleSubmit={handleEmailSave} handleChange={handleEmailChange} />
      </InfoCard>
      {showEquipmentAlert && !hasFiber && (
        <SectionAlert className={styles['sectionAlert']}>
          <div>
            <Txt component="span" variant="smallBold" color="regular">
              {t('contactInfo.notice')}
            </Txt>
            <Txt component="span" variant="smallRegular" color="regular">
              {t('contactInfo.alert')}
            </Txt>
          </div>
        </SectionAlert>
      )}
    </div>
  );
};

export default ContactInformation;
